import { useMemo, useState, ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Text } from 'react-aria-components';
import PageTitle from 'src/components/creator/PageTitle';
import AddCreatorButton from 'src/components/creator/AddCreatorButton';
import OrderRequestsTable, {
  useSortedOrderRequests,
  COLUMN_DEFINITIONS,
  DEFAULT_COLUMN_VISIBILITY,
} from 'src/components/creator/OrderRequestsTable';
import RequestReorderPopover from 'src/components/creator/RequestReorderPopover';
import PurchaseOrderPopover from 'src/components/creator/PurchaseOrderPopover';
import UploadPOButton from 'src/components/creator/UploadPOButton';
import EditColumnsButton from 'src/components/creator/EditColumnsButton';
import useColumnVisibility from 'src/hooks/useColumnVisibility';
import {
  AutoComplete,
  Button,
  IconButton,
  PageLoader,
  Paging,
  TextField,
} from 'src/components/ui';
import { iconStyles } from 'src/components/ui/Field';
import PaperIcon from 'src/assets/icons/file-paper-document.svg';
import SearchIcon from 'src/assets/icons/search.svg';
import CloseIcon from 'src/assets/icons/close.svg';
import REQUESTS_QUERY from 'src/graphql/query/order-requests.graphql';
import useUser from 'src/hooks/useUser';
import filterOrderRequests, { canSeeNetPrice } from 'src/utils/order-requests';

import { ClientType } from 'src/types/client';

import type { OrderRequest } from 'src/types/order';
import type { Bookstore } from 'src/types/institution';

const RequestsPage = () => {
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();
  const [reorder, setReorder] = useState<number | null>(null);
  const [purchaseOrderId, setPurchaseOrderId] = useState<number | null>(null);
  const [selectedBookstores, setSelectedBookstores] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { loading, list, totalCount } = useFilteredRequests({
    selectedBookstores,
    searchQuery,
  });
  const { visibility, toggle, isPending } = useColumnVisibility(
    'requests_column_visibility',
    DEFAULT_COLUMN_VISIBILITY
  );
  const { sortedList, sortDescriptor, onSortChange } =
    useSortedOrderRequests(list);

  const paginatedList = useMemo(
    () => sortedList.slice((page - 1) * pageSize, page * pageSize),
    [sortedList, page, pageSize]
  );

  const isFaculty = user?.clientType === ClientType.CreatorFaculty;
  const isFacultyCustomer = isFaculty && user?.isFacultyCustomer;
  const isOrganizationMember = [
    ClientType.CreatorOrgAdmin,
    ClientType.CreatorOrgRepresentative,
  ].includes(user?.clientType);
  // An institution admin invites into the institution they manage, which is a
  // different field from `institution`, their own affiliation. The dialog reads
  // that field itself, so the id below is the organization case only — but the
  // button still waits for the managed institution to arrive, rather than
  // opening a dialog with nothing to file against.
  const isInstitutionAdmin =
    user?.clientType === ClientType.CreatorInstitutionAdmin;
  const hasManagedInstitution = Boolean(
    user?.managedInstitution?.institutionId
  );

  if (loading || userLoading) return <PageLoader />;

  let tableContent: ReactNode;
  const showNetPrice = canSeeNetPrice({
    clientType: user?.clientType,
    isFacultyCustomer: user?.isFacultyCustomer,
  });
  const availableColumns = COLUMN_DEFINITIONS.filter(
    (c) => c.key !== 'estimatedPrice' || showNetPrice
  );

  if (list.length > 0) {
    tableContent = (
      <>
        <div className="flex justify-end px-4 py-3">
          <EditColumnsButton
            columns={availableColumns}
            visibility={visibility}
            onToggle={toggle}
          />
        </div>
        <div
          className={`flex-1 min-h-0 pb-6 px-4 transition-opacity duration-150 ${isPending ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <OrderRequestsTable
            aria-label="All Course Packs"
            rows={paginatedList}
            sortDescriptor={sortDescriptor}
            onSortChange={(descriptor) => {
              onSortChange(descriptor);
              setPage(1);
            }}
            showNetPrice={showNetPrice}
            visibleColumns={visibility}
            onExactReorder={(id) => setReorder(id)}
            onPurchaseOrder={
              !isFaculty || isFacultyCustomer
                ? (id) => setPurchaseOrderId(id)
                : undefined
            }
          />
        </div>
        <Paging
          totalItems={list.length}
          pageSize={pageSize}
          page={page}
          pageSizeLabel="Requests per page"
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </>
    );
  } else if (totalCount > 0) {
    tableContent = (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="w-full max-w-[31.25rem] text-surface-alt-90 text-center">
          <Text className="font-bold text-2xl" elementType="h2">
            No results found
          </Text>
          <Text elementType="p" className="font-medium text-base mt-4 mb-6">
            No requests match your search or filter. Try adjusting your search
            terms or clearing the filters.
          </Text>
        </div>
      </div>
    );
  } else {
    tableContent = (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="w-full max-w-[31.25rem] text-surface-alt-90 text-center">
          <Text className="font-bold text-2xl" elementType="h2">
            Order Custom Coursepacks
          </Text>
          <Text elementType="p" className="font-medium text-base mt-4 mb-6">
            At LAD Custom Publishing, we specialize in creating custom print and
            digital materials to meet the unique educational needs of our
            customers. Click below to start an order.
          </Text>
          <Button
            variant="success"
            onPress={() => navigate({ to: '/requests/create' })}
          >
            Create Order
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle title="Order Requests" />

      <div className="flex items-center gap-2 px-4 min-h-20 py-3 flex-wrap-reverse">
        <TextField
          aria-label="Search requests"
          placeholder="Search by course, professor, term, or SKU..."
          value={searchQuery}
          onChange={(val) => {
            setSearchQuery(val);
            setPage(1);
          }}
          type="search"
          Icon={SearchIcon}
          rightSlot={
            searchQuery ? (
              <IconButton
                className={iconStyles({
                  className: 'right-0.5 !bg-transparent',
                })}
                aria-label="Clear search"
                onPress={() => {
                  setSearchQuery('');
                  setPage(1);
                }}
                Icon={CloseIcon}
              />
            ) : null
          }
          className="flex-1 max-w-[30rem] min-w-full lg:min-w-0"
        />
        <BookstoresMultiSelect
          bookstores={user?.organization?.bookstores}
          onChange={(bIds) => {
            setSelectedBookstores(bIds);
            setPage(1);
          }}
          className="min-w-full lg:min-w-0 lg:w-fit"
        />
        <Button
          onPress={() => navigate({ to: '/requests/create' })}
          variant="success"
          IconLeft={PaperIcon}
          className="ml-auto"
        >
          Create Order
        </Button>
        {/* An institution admin adds faculty, but has no purchase orders to
            upload — so the two buttons part company. */}
        {isOrganizationMember && <UploadPOButton />}
        {(isOrganizationMember ||
          (isInstitutionAdmin && hasManagedInstitution)) && (
          <AddCreatorButton
            institutionId={String(user?.institutionId ?? '')}
            inviteOnly
          />
        )}
      </div>

      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {(searchQuery || selectedBookstores.length > 0) &&
          `${list.length} ${list.length === 1 ? 'result' : 'results'} found`}
      </div>

      <div className="bg-surface-alt rounded-xl shadow-3 flex-1 min-h-0 overflow-hidden flex flex-col">
        {tableContent}
      </div>
      {Boolean(reorder) && (
        <RequestReorderPopover
          requestId={String(reorder)}
          onClose={() => setReorder(null)}
        />
      )}
      {Boolean(purchaseOrderId) && (
        <PurchaseOrderPopover
          requestId={String(purchaseOrderId)}
          onClose={() => setPurchaseOrderId(null)}
        />
      )}
    </>
  );
};

const BookstoresMultiSelect = ({
  bookstores = [],
  onChange,
  className = 'w-full',
}: {
  bookstores?: Bookstore[];
  className?: string;
  onChange: (list: any[]) => void;
}) => {
  const options = bookstores.map((bookstore) => ({
    value: bookstore.bookstoreId,
    label: bookstore.name,
  }));

  return (
    <AutoComplete
      menuPosition="fixed"
      menuPortalTarget={document.body}
      menuShouldScrollIntoView={false}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        container: (base) => ({ ...base, width: 'auto' }),
        control: (base) => ({
          ...base,
          width: 'max-content',
          minWidth: '11rem',
          maxWidth: '30rem',
        }),
      }}
      options={options}
      onChange={(list: any) => {
        onChange(list.map((o: any) => Number(o.value)));
      }}
      name="bookstores"
      aria-label="Filter by store"
      placeholder="Filter by Store"
      className={className}
      maxMenuHeight={180}
      isMulti
    />
  );
};

const useFilteredRequests = ({
  selectedBookstores = [],
  searchQuery = '',
}: {
  selectedBookstores: number[];
  searchQuery: string;
}) => {
  const { loading, data } = useQuery(REQUESTS_QUERY);

  const { filtered, totalCount } = useMemo(() => {
    const requestList = (data?.requests ?? []) as OrderRequest[];
    return {
      filtered: filterOrderRequests(requestList, {
        selectedBookstores,
        searchQuery,
      }),
      totalCount: requestList.length,
    };
  }, [data?.requests, selectedBookstores, searchQuery]);

  return {
    loading,
    list: filtered,
    totalCount,
  };
};

export const Route = createFileRoute('/_authenticated/_creator/requests/')({
  component: RequestsPage,
});
